import React, { useEffect, useState, useContext, useCallback } from 'react'
import { IconButton, Tooltip } from '@mui/material'
import { CheckCircle, Delete, Edit, InsertLink, Star, StarOutline } from '@mui/icons-material'
import DownloadIcon from '@mui/icons-material/Download';
import RoleContext from '../../../auth/RoleContext.js';
import { ROLES } from '../../../auth/role.js';
import ResourcePreviewPopover from '../ResourcePreviewPopover.js';
import { ResourceStatus, getFileOpenUrl } from '../../data/index.js';
import UserContext from '../../../auth/UserContext.js';
import {default as EventBus} from '../../../utils/EventBus.js'
import Event from '../../../utils/EventNames.js'
import CacheContext from '../../../cache/CacheContext.js';
import { SAFETYRESOURCES } from '../../../users/data/preferences.js';


const FileActionIconButtons = ({file, category, suppressFavorites = false, onDeleteDocument = undefined, onEditResourceProperties = undefined, onApproveDraft = undefined}) => {
    const documentId = file?.driveItem?.id
    const listItemId = file.eTag.replace(/"/g, '').split(',')[0]
    const status = file?.fields?.Status
    const createdByEmail = file?.createdBy?.user?.email
    const [copyFade, setCopyFade] = useState(false)
    const [isAddingFavorite, setIsAddingFavorite] = useState(false)
    const [isRemovingFavorite, setIsRemovingFavorite] = useState(false)
    const [favorites, setFavorites] = useState([])

    const roleDetails = useContext(RoleContext)
    const userContext = useContext(UserContext)
    const cache = useContext(CacheContext)
    const FAVORITES_CACHE_KEY = `${SAFETYRESOURCES}Favorites`

    const openFile = async (file) => {
      try {
      const url = await getFileOpenUrl(file)
      if (url) window.open(url, '_blank')
      } catch (error) {
        EventBus.emit(Event.ALERT, { "text": `Error: ${error.message}`, "style": "error", "duration": 5000 })
      }
    }
    const downloadFile = (file) => {
      const url = file?.driveItem['@microsoft.graph.downloadUrl'] ?? undefined
      if (url) window.open(url, '_blank')
    }

    useEffect(() => {
      if (cache) {
        const favs = cache.getItem(FAVORITES_CACHE_KEY) ?? []
        setFavorites(favs)
      }
    }, [])

    const isFavorite = useCallback((_documentId) => {
      return (favorites ?? []).includes(_documentId)
    }, [favorites])
    
    const handleAddFavorite = async (documentId) => {
      if (!suppressFavorites) {
        setIsAddingFavorite(true)
        const favs = await userContext.saveFavoriteItem(userContext.uniqueId, SAFETYRESOURCES, documentId)
        setFavorites(favs)
        // cache.setItem(FAVORITES_CACHE_KEY, favs)
      }

      setIsAddingFavorite(false)
    }

    const handleRemoveFavorite = async (documentId) => {
      if (!suppressFavorites) {
        setIsRemovingFavorite(true)
        const favs = await userContext.removeFavoriteItem(userContext.uniqueId, SAFETYRESOURCES, documentId)
        // cache.setItem(FAVORITES_CACHE_KEY, favs)
        setFavorites(favs)
      }

      setIsRemovingFavorite(false)
    }

    const handleCopyPortalLink = (target, resourceId) => {
      const url = window.location.origin + window.location.pathname
      navigator.clipboard.writeText(`${url}?resourceId=${resourceId}`)
      setCopyFade(true)
      setTimeout(() => setCopyFade(false), 1000)
    }

    const handleEditResourceProperties = (rId) => {
        if (onEditResourceProperties !== undefined) {
          onEditResourceProperties(rId, category)
        }
      }
    
      const handleDeleteDocument = (rId, isPublished) => {
        if (onDeleteDocument !== undefined) {
          onDeleteDocument(rId, category, isPublished)
        }
      }
    
      const handleApproveDraft = (rId, _category) => {
        if (onApproveDraft !== undefined) {
          onApproveDraft(rId, _category ?? category)
        }
      }

    // const documentId, openUrl, downloadUrl, status, createdByEmail
    return (
      <>
      { status !== ResourceStatus.DRAFT && 
        <>
        { !suppressFavorites &&
        <>
        { !isFavorite(documentId) && 
          <Tooltip arrow placement='left' title={`Mark as a Favorite`}>
            <span>
            <IconButton onClick={() => { handleAddFavorite(documentId) }} disabled={isAddingFavorite}><StarOutline /></IconButton>
            </span>
          </Tooltip>
        }
        { isFavorite(documentId) && 
          <Tooltip arrow placement='left' title={`Remove Favorite`}>
            <span>
            <IconButton onClick={() => { handleRemoveFavorite(documentId) }} disabled={isRemovingFavorite}><Star /></IconButton>
            </span>
          </Tooltip>
        }
        </>
        }
        <Tooltip arrow placement='left' title={`Copy a Portal Link to Clipboard`}>
          <IconButton onClick={(e) => { handleCopyPortalLink(e.target, listItemId) }}
            sx={{
              '& .MuiSvgIcon-root': {
                transition: 'color 0.5s ease-in-out',
                fill: copyFade ? 'lightgreen' : 'currentColor',
              },
            }}><InsertLink /></IconButton>
          </Tooltip>
        </>
        
      }
      { (roleDetails.hasRole([ROLES.QSE_SAFETY_MANAGER, ROLES.MFI_ADMIN, ROLES.ADMIN]
        || [userContext.email, userContext.userName].includes(createdByEmail))
        && <Tooltip arrow placement='left' title={`Edit File Properties`}>
          <span>
            <IconButton onClick={() => handleEditResourceProperties(documentId)}><Edit /></IconButton>
          </span>
        </Tooltip>
      ) }
      <ResourcePreviewPopover resourceId={documentId} key={documentId} triggerLabel='Open a Preview' />
      {/* <Tooltip arrow placement='left' title={`Open in New Window`}>
        <IconButton onClick={() => openFile(file)} ><OpenInNewIcon /></IconButton>
      </Tooltip> */}
      <Tooltip arrow placement='left' title={`Download File`}>
        <IconButton onClick={() => downloadFile(file)}><DownloadIcon /></IconButton>
      </Tooltip>

      {roleDetails.hasRole([ROLES.QSE_SAFETY_MANAGER, ROLES.MFI_ADMIN, ROLES.ADMIN])
        && <>
          {status === ResourceStatus.DRAFT
            && <Tooltip arrow placement='left' title='Approve and Publish'>
                <IconButton onClick={() => handleApproveDraft(documentId)}><CheckCircle color='success'/></IconButton>
              </Tooltip>
          }
          {status !== ResourceStatus.DRAFT
            && <Tooltip arrow placement='left' title='Delete File'>
                  <IconButton onClick={() => handleDeleteDocument(documentId)}><Delete color='error'/></IconButton>
                </Tooltip>
          }
        </>
      }
      {status === ResourceStatus.DRAFT
        && <Tooltip arrow placement='left' title='Delete File'>
            <IconButton onClick={() => handleDeleteDocument(documentId)}><Delete color='error'/></IconButton>
          </Tooltip>
      }
      </>
    )
  }

export default FileActionIconButtons